import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonIcon from '@mui/icons-material/Person';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TelegramIcon from '@mui/icons-material/Telegram';
import Link from '@mui/material/Link';
import { Box, Card, Grid, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LandingAccountGuideSection from './LandingAccountGuideSection';
import LandingVideoLessonsSection from './LandingVideoLessonsSection';

const painPointKeys = [
  'landing.pain.q1',
  'landing.pain.q2',
  'landing.pain.q3',
  'landing.pain.q5',
  'landing.pain.q6',
  'landing.pain.q7',
  'landing.pain.q9',
  'landing.pain.q10'
];

const detailCards = [
  {
    icon: PersonIcon,
    titleKey: 'landing.detail.client.title',
    descKey: 'landing.detail.client.desc',
    itemsKey: 'landing.detail.client.items'
  },
  {
    icon: Inventory2Icon,
    titleKey: 'landing.detail.movement.title',
    descKey: 'landing.detail.movement.desc',
    itemsKey: 'landing.detail.movement.items'
  },
  {
    icon: CalendarMonthIcon,
    titleKey: 'landing.detail.daily.title',
    descKey: 'landing.detail.daily.desc',
    itemsKey: 'landing.detail.daily.items'
  },
  {
    icon: NotificationsActiveIcon,
    titleKey: 'landing.detail.reminders.title',
    descKey: 'landing.detail.reminders.desc',
    itemsKey: 'landing.detail.reminders.items'
  },
  {
    icon: StorefrontIcon,
    titleKey: 'landing.detail.branches.title',
    descKey: 'landing.detail.branches.desc',
    itemsKey: null
  },
  {
    icon: TelegramIcon,
    titleKey: 'landing.detail.telegram.title',
    descKey: 'landing.detail.telegram.desc',
    itemsKey: 'landing.detail.telegram.items'
  }
];

export default function LandingAboutSection() {
  const { t } = useTranslation();

  return (
    <Box id="about-program" sx={{ scrollMarginTop: 96, pt: { xs: 6, md: 10 } }}>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h3" fontWeight={800}>
          {t('Dastur haqida')}
        </Typography>
        <Typography variant="h5" color="primary.main" fontWeight={700}>
          {t('Arendatorlar uchun universal tizim')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 900 }}>
          {t('landing.about.intro')}
        </Typography>
      </Stack>

      <Card sx={{ p: { xs: 3, md: 4 }, mb: 6, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t('landing.pain.title')}
        </Typography>
        <Grid container spacing={1}>
          {painPointKeys.map((key) => (
            <Grid item xs={12} sm={6} key={key}>
              <Typography variant="body1">• {t(key)}</Typography>
            </Grid>
          ))}
        </Grid>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3 }}>
          {t('landing.pain.footer')}
        </Typography>
      </Card>

      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={700}>
          {t('landing.hero.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('landing.about.variant2')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('landing.about.variant3')}
        </Typography>
      </Stack>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        {t('landing.features.title')}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 6 }}>
        {detailCards.map(({ icon: Icon, titleKey, descKey, itemsKey }) => (
          <Grid item xs={12} md={6} key={titleKey}>
            <Card sx={{ p: 3, height: '100%', borderRadius: 3 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                    flexShrink: 0
                  }}>
                  <Icon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {t(titleKey)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: itemsKey ? 1.5 : 0 }}>
                    {t(descKey)}
                  </Typography>
                  {itemsKey ? (
                    <List dense disablePadding>
                      {t(itemsKey)
                        .split('|')
                        .map((item) => (
                          <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
                            <ListItemText primary={`• ${item.trim()}`} />
                          </ListItem>
                        ))}
                    </List>
                  ) : null}
                </Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>

      <LandingAccountGuideSection />

      <LandingVideoLessonsSection />

      <Box
        component="footer"
        sx={{
          mt: 2,
          py: { xs: 4, md: 5 },
          px: { xs: 2, md: 4 },
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t('landing.contact.title')}
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TelegramIcon color="primary" />
            <Link
              href="https://t.me/system24admin"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              fontWeight={600}>
              {t('landing.contact.telegram')}
            </Link>
          </Stack>
          <Link href="tel:+998995281829" underline="hover" fontWeight={600} color="text.primary">
            {t('landing.contact.phone')}
          </Link>
        </Stack>
      </Box>
    </Box>
  );
}
